import React, {
  ComponentProps,
  MouseEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { captureException } from "@sentry/browser";
import queryString from "query-string";
import { MapRef, NavigationControl } from "react-map-gl";
import { useLocation } from "react-router";
import { Bounds } from "viewport-mercator-project";
import { FEATURE_FLAG_NAME } from "common/constants";
import { MapsForAccountQuery } from "../../generated/graphql";
import { useCanGeolocate } from "../../hooks/useCanGeolocate";
import { useComposedRefs } from "../../hooks/useComposedRefs";
import { isSmall } from "../../utils/size";
import AccountActivityFeed from "../ActivityFeed/AccountActivityFeed";
import { AuthContext } from "../Authorization/AuthContext";
import { VisuallyHidden } from "../Common/VisuallyHidden";
import { OldOrNewSearch, SearchResultProps } from "../Search";
import { CreatePropertyTool } from "./CreatePropertyTool";
import { ExitButton } from "./ExitButton";
import GeolocateBanner from "./GeolocateBanner";
import { GeolocationTool } from "./GeolocationTool";
import { LayerConfigs, LayeredMap, Point } from "./LayeredMap";
import { ClickObjectMenuLayerConfig } from "./LayeredMap/clickObjectMenuLayer";
import { CustomMapLayerConfig } from "./LayeredMap/customMapLayer";
import { MarkerLayerConfig } from "./LayeredMap/markerLayer";
import { ParcelLayerConfig } from "./LayeredMap/parcelLayer";
import { PropertyLayerConfig } from "./LayeredMap/propertyLayer";
import { SavedViewsLayerConfig } from "./LayeredMap/savedViewsLayer";
import { LayerContext } from "./layers";
import LayersPanel from "./LayersPanel";
import { LegendControlTool } from "./LegendControl";
import { MeasureTool } from "./MeasureTool";
import { SavedViewBanner } from "./SavedViewBanner";
import { UserDeviceLocationLegendControl } from "./UserDeviceLocation/UserDeviceLocationLegendControl";
import { DeviceLocationsConfig } from "./UserDeviceLocation/useUserDeviceLocations";
import { onMapAvailable } from "./utils/mapUtils";
import useViewport from "./utils/viewportHook";

const useQueryParams = (
  account: NonNullable<MapsForAccountQuery["account"]>,
) => {
  const location = useLocation();
  const params = queryString.parse(location.search);
  const longitude = parseFloat(params.lng as string);
  const latitude = parseFloat(params.lat as string);
  let selectedFirmId = params.firmId as Maybe<string>;
  if (!account.firms.some(accountFirm => accountFirm.id === selectedFirmId)) {
    selectedFirmId = account.firms.find(f => f.isDefault)?.id || null;
  }

  return {
    latitude,
    longitude,
    selectedFirmId,
  };
};

interface FullpageMapProps {
  account: NonNullable<MapsForAccountQuery["account"]>;
  markerConfig: MarkerLayerConfig;
  parcelConfig: ParcelLayerConfig;
  propertyConfig: PropertyLayerConfig;
  savedViewsConfig: SavedViewsLayerConfig;
  deviceLocationsConfig?: DeviceLocationsConfig;
  customMapsConfig: CustomMapLayerConfig;
  clickObjectMenuConfig: ClickObjectMenuLayerConfig;
  infoPanelOpen: boolean;
  onSearchResult: (data: SearchResultProps) => void;
  handleExitClick?: () => void;
  viewportDimensions?: () => { width: number; height: number };
}

const FullPageMap = React.forwardRef(
  (
    {
      account,
      markerConfig,
      parcelConfig,
      propertyConfig,
      savedViewsConfig,
      deviceLocationsConfig,
      customMapsConfig,
      clickObjectMenuConfig,
      infoPanelOpen,
      onSearchResult,
      handleExitClick,
      viewportDimensions,
    }: FullpageMapProps,
    ref: React.Ref<any>,
  ) => {
    const { isFeatureEnabled, isPublic } = useContext(AuthContext);
    const isMeasureToolEnabled = isFeatureEnabled(
      FEATURE_FLAG_NAME.MEASURE_TOOL_WEB,
    );

    const {
      toggleLayer,
      visibleFIRM,
      visibleRaster: getVisibleRaster,
      visibleSavedViews: getVisibleSavedViews,
      visibleLayerIdsForGroup,
      measureToolDispatch,
      measureToolState,
      approximateBfeToolState,
      approximateBfeToolDispatch,
      geometryBounds,
      isSearching,
    } = useContext(LayerContext);

    const [layersPanelVisible, toggleLayersPanel] = useState(false);
    const [showGeolocateBanner, setShowGeolocateBanner] = useState(false);
    const [savedViewIdToShowBannerFor, setSavedViewIdToShowBannerFor] =
      useState(savedViewsConfig.value?.[0]?.id);
    const visibleSavedViewIds = visibleLayerIdsForGroup("savedViews");
    useEffect(() => {
      if (
        visibleSavedViewIds.length === 1 &&
        savedViewIdToShowBannerFor === visibleSavedViewIds[0]
      ) {
        return;
      }
      setSavedViewIdToShowBannerFor(undefined);
    }, [JSON.stringify(visibleSavedViewIds)]);

    const visibleRaster = getVisibleRaster();

    const { latitude, longitude, selectedFirmId } = useQueryParams(account);

    const internalMapRef = useRef<MapRef | null>(null);
    const composedRef = useComposedRefs(internalMapRef, ref);

    if (
      measureToolState.measureToolMode === "distance" ||
      measureToolState.measureToolMode === "raster" ||
      approximateBfeToolState?.mode === "on"
    ) {
      parcelConfig = {
        ...parcelConfig,
        interactive: { hover: false, click: false },
      };
      propertyConfig = {
        ...propertyConfig,
        interactive: { hover: false, click: false },
      };
      savedViewsConfig = {
        ...savedViewsConfig,
        interactive: { hover: false, click: false },
      };
      customMapsConfig = {
        ...customMapsConfig,
        interactive: { hover: false },
      };
    }

    const layers: LayerConfigs = {
      parcels: parcelConfig,
      firms: {
        firms: account.firms,
        onClick: crossSection => {
          approximateBfeToolDispatch?.({
            type: "toggleCrossSection",
            data: { crossSection },
          });
        },
        interactive: {
          click:
            approximateBfeToolState?.mode === "on" &&
            !approximateBfeToolState.disableMapInteractions,
          hover: approximateBfeToolState?.mode === "on",
        },
      },
      measure: {
        onClick: coordinates => {
          measureToolDispatch({
            type: "setCoordinates",
            data: { coordinates },
          });
        },
        interactive: {
          click:
            (measureToolState.measureToolMode === "distance" ||
              measureToolState.measureToolMode === "raster") &&
            !measureToolState.loading,
        },
      },
      raster: {
        rasters: account.rasters,
      },
      accountBoundary: {},
      marker: markerConfig,
      properties: propertyConfig,
      contour: {},
      cbrs: {},
      clickObjectMenu: clickObjectMenuConfig,
      customMaps: customMapsConfig,
      savedViews: savedViewsConfig,
      savedViewsCluster: {
        value: savedViewsConfig.value,
        onClick: async (lngLat: [number, number]) => {
          await zoomToPoint(
            { longitude: lngLat[0], latitude: lngLat[1] },
            viewport.zoom + 1,
          );
        },
        interactive: {
          click: !!savedViewsConfig.interactive?.click,
          hover: false,
        },
      },
      deviceLocations: deviceLocationsConfig ?? {},
    };

    // While we show the marker based on the passed in values, not URL lat/long,
    // We need to set the zoom to 18 if there is a lat/long in the URL.
    // Otherwise, the map will render completely zoomed out and set the zoom to 0
    const defaultZoom = latitude && longitude ? 18 : undefined;

    const { viewport, setViewport, setViewportWithTransition } = useViewport({
      zoom: defaultZoom,
      latitude,
      longitude,
      bounds: account.bounds as Bounds,
      viewportDimensions,
    });

    // Create a ref to keep track of previous zoom level. This is so we can
    // compare it against the current zoom level and only announce it to screen
    // readers if the zoom level has changed.
    const prevZoomRef = useRef<number>(viewport.zoom);
    const zoomAnnouncementRef = useRef<HTMLDivElement>(null);

    // Set zoom level in aria-live region when it changes
    useEffect(() => {
      const prevZoom = prevZoomRef.current;
      const currentZoom = viewport.zoom;

      if (prevZoom !== currentZoom && zoomAnnouncementRef.current) {
        // Round down, since Mapbox's zoom level starts at 0
        const roundedZoom = Math.floor(currentZoom);
        zoomAnnouncementRef.current.textContent = `Zoom level ${roundedZoom}`;
      }

      prevZoomRef.current = currentZoom;
    }, [viewport.zoom]);

    const handleLayersToggle = (evt?: MouseEvent | KeyboardEvent) => {
      evt?.preventDefault();
      toggleLayersPanel(!layersPanelVisible);
    };

    const baseMapStyle = account.baseMaps.find(
      map => map.id === visibleLayerIdsForGroup("baseMaps")[0],
    )?.mapboxStyle;

    const geolocateClicked = async () => {
      let geolocationPermissions = { state: "denied" };
      const excludedErrorMessages = [
        "Permissions::query does not support this API",
        "The operation is not supported.",
      ];

      // the availability of the geolocation API is super spotty
      // for mobile iOS in particular for some reason - perhaps
      // specific phone settings? - and so we just blanket this
      // with some pokemon exception handling
      try {
        geolocationPermissions = await navigator.permissions.query({
          name: "geolocation",
        });
      } catch (error) {
        if (
          error instanceof Error &&
          !excludedErrorMessages.some(message =>
            error.message.includes(message),
          )
        ) {
          captureException(error);
        }
      }

      if (geolocationPermissions.state === "denied") {
        setShowGeolocateBanner(true);
      }
    };

    const zoomToPoint = async (point: Point, zoom: number = 18) => {
      await onMapAvailable(internalMapRef, () => {
        setViewportWithTransition({
          ...point,
          zoom,
        });
      });
    };

    const zoomToBounds = async (bounds: Bounds) => {
      await onMapAvailable(internalMapRef, () => {
        setViewportWithTransition(
          {
            bounds,
          },
          true,
        );
      });
    };

    useEffect(() => {
      if (geometryBounds) {
        void zoomToBounds(geometryBounds);
      } else if (markerConfig.value || parcelConfig.value?.point) {
        void zoomToPoint(markerConfig.value || parcelConfig.value?.point!);
      }
    }, [
      geometryBounds,
      markerConfig.value?.latitude,
      markerConfig.value?.longitude,
      parcelConfig.value?.point?.latitude,
      parcelConfig.value?.point?.longitude,
    ]);

    useEffect(() => {
      if (selectedFirmId) {
        toggleLayer({ group: "firms", id: selectedFirmId, isVisible: true });
      }
    }, [selectedFirmId]);

    // This check is necessary because in some very particular and mysterious
    // circumstances, the navigator.permissions.query Promise inside of
    // GeolocateControl rejects and is unhandled, which raises an alert in
    // Sentry. To prevent this, safely check if the permission query *will* fail
    // and only show the control if it does not.
    const [canGeolocate] = useCanGeolocate();

    const activeFloodLayerName =
      visibleFIRM()?.name ?? visibleRaster?.layerName;

    const hideNavigationControls =
      (measureToolState.measureToolMode === "raster" ||
        measureToolState.measureToolMode === "distance") &&
      isSmall();

    const visibleSavedViews = getVisibleSavedViews();

    return (
      <>
        {/* Used to announce Map zoom level changes to screen readers */}
        <VisuallyHidden
          ref={zoomAnnouncementRef}
          aria-live="polite"
          aria-atomic="true"
        />

        <LayeredMap
          ref={composedRef}
          height="100%"
          width="100%"
          baseMapStyle={baseMapStyle}
          account={account}
          layers={layers}
          viewport={viewport}
          setViewport={setViewport}
          topLeftMapOverlayItems={[
            <OldOrNewSearch
              halfMap={false}
              handleResultClick={onSearchResult}
            />,
            !isPublic && !isSearching && !infoPanelOpen ?
              <AccountActivityFeed />
            : null,
          ].filter(Boolean)}
          rightPanelOverlayItems={
            layersPanelVisible ?
              [
                <LayersPanel
                  close={handleLayersToggle}
                  firms={account.firms}
                  rasters={account.rasters}
                  baseMaps={account.baseMaps}
                  accountDocumentTypes={account.accountDocumentTypes}
                  accountPropertyWarningDefinitions={
                    account.accountPropertyWarningDefinitions
                  }
                  savedViews={account.savedViews}
                  disableRasterToggle={measureToolState.loading}
                  disableFirmToggle={approximateBfeToolState?.mode !== "off"}
                />,
              ]
            : []
          }
          topRightMapOverlayItems={
            handleExitClick ?
              [<ExitButton handleExitClick={handleExitClick} />]
            : undefined
          }
          bottomRightMapOverlayItems={
            !hideNavigationControls && !layersPanelVisible ?
              [
                deviceLocationsConfig ?
                  <UserDeviceLocationLegendControl
                    setViewport={setViewportWithTransition}
                    deviceLocationsConfig={deviceLocationsConfig}
                  />
                : null,
                canGeolocate ?
                  <GeolocationTool geolocateClicked={geolocateClicked} />
                : null,
                isMeasureToolEnabled ? <MeasureTool /> : null,
                !isPublic ? <CreatePropertyTool viewport={viewport} /> : null,
                <NavigationControl
                  style={{ position: "relative" }}
                  showCompass={false}
                />,
                <LegendControlTool
                  activeFloodLayerName={activeFloodLayerName}
                  handleLayersToggle={handleLayersToggle}
                />,
              ].filter(Boolean)
            : undefined
          }
        />
        {showGeolocateBanner && !layersPanelVisible && (
          <GeolocateBanner
            onClose={() => {
              setShowGeolocateBanner(false);
            }}
            infoPanelOpen={infoPanelOpen}
          />
        )}

        <SavedViewBanner
          visibleSavedView={visibleSavedViews?.find(
            savedView => savedView.id === savedViewIdToShowBannerFor,
          )}
          zoom={viewport.zoom}
          infoPanelOpen={infoPanelOpen}
        />
        {measureToolState.render?.()}
        {approximateBfeToolState?.render?.()}
      </>
    );
  },
);

export type FullPageMapContainerProps = Omit<
  ComponentProps<typeof FullPageMap>,
  "clickObjectMenuConfig"
>;

export default FullPageMap;
